import {skipToken} from '@reduxjs/toolkit/query';
import {Card, closeCurrentDialog, openDialog} from 'platform/components';
import {Box, Grid, HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query, CustomerResponseBodyV2} from '@dms/api/customer';
import {EMPTY_UTM_PARAMETERS, EntityResourceIds, UtmParameters} from '@dms/api/shared';
import {
  useChangeWorkshopInterestCustomerMutation,
  useGetWorkshopInterestQuery,
  useSelectWorkshopInterestContactPersonMutation,
  useUpdateWorkshopInterestUtmMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {
  CustomerMatchOrCreate,
  CustomerWidgetCard,
  handleApiError,
  Section,
  useWorkshopInterestEditPermissions,
  UtmCard,
} from '@dms/shared';

import {ContactPersonForm} from './ContactPersonForm';

type CustomerProps = {
  interestId: string;
};

export function Customer(props: CustomerProps) {
  const {data: workshopInterest} = useGetWorkshopInterestQuery({id: props.interestId});

  const {data: customer} = useGetCustomerV2Query(
    isNotNil(workshopInterest?.customerId) ? {customerId: workshopInterest.customerId} : skipToken
  );

  const {hasChangeCustomerPermission, hasSelectContactPersonPermission, hasChangeUtmPermission} =
    useWorkshopInterestEditPermissions({interestId: props.interestId});

  const [changeCustomer] = useChangeWorkshopInterestCustomerMutation();
  const [selectContactPerson] = useSelectWorkshopInterestContactPersonMutation();
  const [updateUtm] = useUpdateWorkshopInterestUtmMutation();

  const [showForm, setShowForm] = useState(false);

  const customerId = workshopInterest?.customerId;

  const isInterestClosed =
    workshopInterest?.state === 'CLOSED' || workshopInterest?.state === 'UNSUCCESSFUL';

  const onCustomer = (selectedCustomer: CustomerResponseBodyV2) =>
    changeCustomer({
      id: props.interestId,
      changeWorkshopInterestCustomerRequestBody: {
        customerId: selectedCustomer.id,
      },
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const handleCustomerChange = () =>
    openDialog(
      <CustomerMatchOrCreate
        data-testid={testIds.workshop.interestDetail('customer-match')}
        onCustomer={onCustomer}
      />,
      {title: i18n.t('entity.customer.labels.customer')}
    );

  const handleUtmSubmit = (values: UtmParameters) =>
    updateUtm({
      id: props.interestId,
      updateWorkshopInterestUtmRequestBody: values,
    })
      .unwrap()
      .then(closeCurrentDialog)
      .catch(handleApiError);

  const toggleContactPerson = (value: boolean) => {
    if (value) {
      setShowForm(true);
      return;
    }

    selectContactPerson({
      id: props.interestId,
      selectWorkshopInterestContactPersonRequestBody: {
        contactPersonId: null,
      },
    })
      .unwrap()
      .then(() => setShowForm(false))
      .catch(handleApiError);
  };

  return (
    <Section>
      <Show when={isNotNil(customer)}>
        <Grid columns={[1, 2]} align="flex-start">
          <VStack spacing={4}>
            <CustomerWidgetCard
              resourceId={EntityResourceIds.workshopInterest}
              recordId={props.interestId}
              isContactPersonHidden
              customerId={customer?.id}
              onCustomer={onCustomer}
              onChange={handleCustomerChange}
              secondStepComponentType="BUSINESS_CASE"
              data-testid={testIds.workshop.interestDetail('customer')}
              isDetailLinkVisible
              canChangeCustomer={hasChangeCustomerPermission}
            />
            <UtmCard
              parameters={workshopInterest?.utm ?? EMPTY_UTM_PARAMETERS}
              onSubmit={handleUtmSubmit}
              isDisabled={isInterestClosed || !hasChangeUtmPermission}
              data-testid={testIds.workshop.interestDetail('customer-utm')}
            />
          </VStack>
          <Card
            data-testid={testIds.workshop.interestDetail('contactPerson')}
            variant="default"
            isExpanded={isNotNil(workshopInterest?.contactPersonId) || showForm}
            control={{
              type: 'switch',
              value: isNotNil(workshopInterest?.contactPersonId),
              onChange: toggleContactPerson,
              'data-testid': testIds.workshop.interestDetail('toggleContactPerson'),
            }}
            title={i18n.t('entity.businessCase.labels.contactPerson')}
          >
            <Card
              data-testid={testIds.workshop.interestDetail('contactPersonInner')}
              variant="inlineGrey"
            >
              {/* eslint-disable-next-line eag/prefer-show -- Pre-existing violation */}
              {isNotNil(customerId) && (
                <ContactPersonForm
                  interestId={props.interestId}
                  customerId={customerId}
                  contactPersonId={workshopInterest?.contactPersonId}
                  isDisabled={!hasSelectContactPersonPermission}
                />
              )}
            </Card>
          </Card>
        </Grid>
      </Show>

      <Show when={!isNotNil(customer)}>
        <HStack justify="center">
          <Box width={188}>
            <CustomerWidgetCard
              onCustomer={onCustomer}
              secondStepComponentType="BUSINESS_CASE"
              data-testid={testIds.workshop.interestDetail('customer')}
              canChangeCustomer={hasChangeCustomerPermission}
            />
          </Box>
        </HStack>
      </Show>
    </Section>
  );
}
